import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContactsListStyles } from '../../../components/contacts/ContactsList.styles';
import { useDispatch, useSelector } from 'react-redux';
import { TApiContactsEntity, TContactsState } from '../../../types/Contacts';
import { IAppState } from '../../../store/Store';
import { filterContactsList, getContactsList } from '../../../actions/ContactActions';
import { debounce } from 'lodash';
import PageContent from '../../../components/PageContent';
import { ContentPage } from '../../../types/Content';
import FormControl from '@mui/material/FormControl';
import { MenuItem, Tab, Tabs, TextField } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import MapIcon from '@mui/icons-material/Map';
import List from '@mui/material/List';
import ContactListItem from '../../../components/contacts/ContactListItem';

type FilterType = 'search' | 'groups' | 'locations';

const ContactListPage: React.FC = (): JSX.Element => {
  const [activeFilterTab, setActiveFilterTab] = useState<FilterType>('search');
  const { t } = useTranslation();
  const classes = useContactsListStyles({});
  const dispatch = useDispatch();
  const contactsState: TContactsState = useSelector<IAppState, TContactsState>(
    state => state.contactsState
  );

  const [allLocations, setAllLocations] = useState<Array<string>>([]);
  const [chosenLocation, setChosenLocation] = useState<string>("-");
  const [allGroups, setAllGroups] = useState<Array<string>>([]);
  const [chosenGroup, setChosenGroup] = useState<string>("-");
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    dispatch(getContactsList());
  }, [dispatch]);

  useEffect(() => {
    const locationSet = new Set<string>();
    const groupSet = new Set<string>();

    contactsState.data?.forEach(contact => {
      contact.accessibleLocations.forEach(location => {
        locationSet.add(location.title);
      });

      contact.accessibleDepartments.forEach(group => {
        groupSet.add(group.title);
      });
    });

    const locationList = Array.from(locationSet).sort();
    const groupList = Array.from(groupSet).sort();

    setAllLocations(locationList);
    setAllGroups(groupList);
  }, [contactsState]);

  const filterList = React.useMemo(
    () => debounce((search: string, location: string, group: string) => {
      dispatch(filterContactsList(search, location === "-" ? "" : location, group === "-" ? "" : group))
    }, 100),
    [dispatch]
  );

  return (
    <>
      <PageContent
        pageType={ContentPage.CONTACTS}
        showAsParagraph={true}
      />

      {!contactsState.loading && (
        <>
          <Box display="flex" flexDirection="column">
            <Tabs
              value={activeFilterTab}
              onChange={(_event, newValue) => setActiveFilterTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab
                icon={<SearchIcon/>}
                label={t("contacts.tab.filter")}
                value="search"
              />
              {allGroups?.length > 0 && (
                <Tab
                  icon={<GroupIcon/>}
                  label={t("contacts.tab.groups")}
                  value="groups"
                />
              )}

              {allLocations?.length > 0 && (
                <Tab
                  icon={<MapIcon/>}
                  label={t("contacts.tab.locations")}
                  value="locations"
                />
              )}
            </Tabs>

            <Box mt={2}>
              {activeFilterTab === 'search' && (
                <TextField
                  value={searchValue}
                  id={"search-filter"}
                  label={t("contacts.search.label")}
                  placeholder={t("contacts.search.placeholder")}
                  className={classes.searchField}
                  onChange={e => {
                    filterList(e.target.value, "", "");
                    setSearchValue(() => e.target.value as string);
                    setChosenGroup("-");
                    setChosenLocation("-");
                  }}
                />
              )}
              {activeFilterTab === 'groups' && (
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>{t("contacts.filter.groups")}</InputLabel>
                  <Select
                    value={chosenGroup}
                    id={"group-filter"}
                    onChange={e => {
                      const newGroup = e.target.value as string;
                      filterList("", "", newGroup);
                      setChosenGroup(newGroup);
                      setSearchValue("");
                      setChosenLocation("-");
                    }}
                    label={t("contacts.filter.groups")}
                  >
                    <MenuItem value={"-"} key={"-"}>
                      {t("contacts.filter.groups.all")}
                    </MenuItem>

                    {allGroups &&
                      allGroups.map(title => (
                        <MenuItem value={title} key={title}>
                          {title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}

              {activeFilterTab === 'locations' && (
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>{t("contacts.filter.location")}</InputLabel>
                  <Select
                    value={chosenLocation}
                    id={"location-filter"}
                    onChange={e => {
                      const newLocation = e.target.value as string;
                      filterList("", newLocation, "");
                      setChosenLocation(newLocation);
                      setSearchValue("");
                      setChosenGroup("-");
                    }}
                    label={t("contacts.filter.location")}
                  >
                    <MenuItem value={"-"} key={"-"}>
                      {t("contacts.filter.locations.all")}
                    </MenuItem>

                    {allLocations &&
                      allLocations.map(title => (
                        <MenuItem value={title} key={title}>
                          {title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>
          <div className={classes.gridList}>
            <List>
              {contactsState.filteredList && contactsState.filteredList.length > 0 && (
                contactsState.filteredList.map((contact: TApiContactsEntity) => (
                  <ContactListItem key={contact.id} contact={contact} />
                ))
              )}
            </List>
          </div>
        </>
      )}
    </>
  );
};

export default ContactListPage;